import { ActionIcon, Avatar, Badge, Button, Loader, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileText } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { CopyTextButton } from 'src/Components/Shared/CopyTextButton';
import { useFundCall } from 'src/Hooks/Queries/useFundCalls';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { fundCallsApi } from 'src/Services/API';
import {
  FundCallDto,
  FundCallLineDtoSendStatusEnum,
  FundCallLineDtoStatusEnum,
  SettlementTargetDtoTypeEnum,
} from 'src/Services/API/generated';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './FundCallDetails.module.scss';

const statusColor = (status: FundCallLineDtoStatusEnum): string => {
  switch (status) {
    case FundCallLineDtoStatusEnum.Settled:
      return 'teal';
    case FundCallLineDtoStatusEnum.NotSent:
      return 'orange';
    case FundCallLineDtoStatusEnum.Excluded:
      return 'gray';
    default:
      return 'blue';
  }
};

/**
 * Versement partiel : le back n'a pas de statut dédié, le front le déduit du couple
 * (amountPaid, amountCalled). Décision Alexis 16/10 : le gros chiffre devient « Reste X » et un
 * badge dédié remplace « À verser ».
 */
const isPartiallyPaid = (line: FundCallDto['lines'][number]): boolean =>
  line.status !== FundCallLineDtoStatusEnum.Settled &&
  line.status !== FundCallLineDtoStatusEnum.Excluded &&
  line.amountPaid > 0 &&
  line.amountCalled - line.amountPaid > 0;

/** Détail d'un appel : où en est chaque propriétaire, et comment enregistrer son versement. */
const FundCallDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fundCallId } = useParams<{ fundCallId: string }>();
  const assetId = useCurrentAssetId();
  const { data: fundCall, isLoading } = useFundCall(fundCallId);

  if (isLoading) return <Loader />;
  if (!fundCall) return null;

  const openPdf = async (lineId: string) => {
    if (!assetId || !fundCallId) return;

    // L'onglet doit être ouvert DANS le geste de l'utilisateur. Ouvert après l'await, le
    // navigateur le bloque comme une popup non sollicitée : il ne se passait rien, et aucune
    // erreur ne s'affichait (bug de recette du 11/09).
    // `noopener` en 3ᵉ argument ferait renvoyer `null` à window.open : on coupe `opener` à la
    // main pour garder la référence tout en gardant la protection.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;

    try {
      const { data } = await fundCallsApi.fundCallsControllerGetLinePdf(
        assetId,
        fundCallId,
        lineId
      );
      if (tab) tab.location.href = data.url;
      // Popups bloquées par le navigateur : on ouvre dans l'onglet courant plutôt que de rester
      // muet.
      else window.location.assign(data.url);
    } catch {
      tab?.close();
      notifications.show({
        color: 'red',
        message: t('fundCalls.details.pdfFailed', "Le PDF n'a pas pu être ouvert."),
      });
    }
  };

  /**
   * Montant d'une ligne, selon ce qui a été versé. Rendu riche (deux lignes quand c'est partiel)
   * plutôt qu'une chaîne, pour hiérarchiser le « Reste X » du détail en-dessous.
   */
  const renderAmount = (line: FundCallDto['lines'][number]) => {
    if (line.status === FundCallLineDtoStatusEnum.Excluded) {
      // Convention de l'équilibre : positif = on lui doit.
      if (line.currentBalance > 0) {
        return (
          <div className={styles.amount}>
            {t('fundCalls.details.owedByHouse', 'La maison lui doit {{amount}}', {
              amount: formatEuros(line.currentBalance),
            })}
          </div>
        );
      }
      return <div className={styles.amount}>{t('fundCalls.details.upToDate', 'À jour')}</div>;
    }

    if (line.status === FundCallLineDtoStatusEnum.Settled) {
      return (
        <div className={styles.amount}>
          {t('fundCalls.details.allPaid', '{{amount}} versés', {
            amount: formatEuros(line.amountCalled),
          })}
        </div>
      );
    }

    if (isPartiallyPaid(line)) {
      return (
        <div className={styles.amountBlock}>
          <div className={styles.amountRemaining}>
            {t('fundCalls.details.remaining', 'Reste {{amount}}', {
              amount: formatEuros(line.amountCalled - line.amountPaid),
            })}
          </div>
          <div className={styles.amountBreakdown}>
            {t('fundCalls.details.breakdown', '{{paid}} versés sur {{called}}', {
              paid: formatEuros(line.amountPaid),
              called: formatEuros(line.amountCalled),
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.amount}>
        {t('fundCalls.details.toPay', '{{amount}} à verser', {
          amount: formatEuros(line.amountCalled),
        })}
      </div>
    );
  };

  /**
   * Le bouton « Copier le message de relance » n'a de sens que sur une ligne où il reste quelque
   * chose à régler. Sur une ligne versée, écartée ou où la maison doit, le relancer serait un
   * contre-sens (recette Alexis 16/10).
   */
  const canCopyReminder = (line: FundCallDto['lines'][number]): boolean =>
    line.status !== FundCallLineDtoStatusEnum.Settled &&
    line.status !== FundCallLineDtoStatusEnum.Excluded &&
    line.amountCalled - line.amountPaid > 0;

  const renderStatusBadge = (line: FundCallDto['lines'][number]) => {
    if (isPartiallyPaid(line)) {
      return (
        <Badge variant="light" color="blue">
          {t('fundCalls.details.partiallyPaid', 'Versé en partie')}
        </Badge>
      );
    }
    return (
      <Badge variant="light" color={statusColor(line.status)}>
        {t(`fundCalls.lineStatus.${line.status}`)}
      </Badge>
    );
  };

  /**
   * Where the e-mail of the line went, so the manager no longer has to ask the members. There is
   * no per-line resend route: on failure the manager relays the reminder with « Copier le
   * message de relance ». An excluded line is never sent, so it says nothing.
   */
  const sendInfo = (
    line: FundCallDto['lines'][number]
  ): { label: string; failed: boolean } | null => {
    if (line.status === FundCallLineDtoStatusEnum.Excluded) return null;

    switch (line.sendStatus) {
      case FundCallLineDtoSendStatusEnum.Sent:
        return {
          failed: false,
          label: line.emailSentAt
            ? t('fundCalls.details.send.sentToOn', 'Envoyé à {{email}} le {{date}} à {{time}}', {
                email: line.sentTo ?? '',
                date: dayjs(line.emailSentAt).format('DD/MM'),
                time: dayjs(line.emailSentAt).format('HH:mm'),
              })
            : t('fundCalls.details.send.sentTo', 'Envoyé à {{email}}', {
                email: line.sentTo ?? '',
              }),
        };
      case FundCallLineDtoSendStatusEnum.Failed:
        return {
          failed: true,
          label: t(
            'fundCalls.details.send.failed',
            "Échec d'envoi : copiez le message de relance pour le transmettre vous-même"
          ),
        };
      case FundCallLineDtoSendStatusEnum.NotSent:
        return {
          failed: false,
          label:
            line.party.type === SettlementTargetDtoTypeEnum.PlaceholderMember
              ? t('fundCalls.details.send.notRegistered', 'Non inscrit — PDF à transmettre')
              : t('fundCalls.details.send.noEmail', 'Pas d’e-mail envoyé — PDF à transmettre'),
        };
      default:
        return { failed: false, label: t('fundCalls.details.send.notSent', 'Non envoyé') };
    }
  };

  /**
   * De = the member of the line ; Vers = the house account named in « Pour régler », or the
   * issuer when the asset has none. Both stay editable in the drawer. The line id makes the
   * API move the line to paid — without it the payment was recorded but the line stayed due.
   */
  const recordPaymentState = (line: FundCallDto['lines'][number]) => ({
    amount: line.amountCalled - line.amountPaid,
    ...(line.party.type === SettlementTargetDtoTypeEnum.PlaceholderMember
      ? { fromPlaceholderMemberId: line.party.id }
      : { fromUserId: line.party.id }),
    ...(fundCall.bankAccount
      ? { toBankAccountId: fundCall.bankAccount.id }
      : { toUserId: fundCall.createdBy.id }),
    fundCallLineId: line.id,
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{fundCall.title}</div>
        <div className={styles.meta}>
          {fundCall.periodLabel}
          {fundCall.dueDate
            ? ` · ${t('fundCalls.details.dueOn', 'à régler avant le {{date}}', {
                date: dayjs(fundCall.dueDate).format('DD/MM/YYYY'),
              })}`
            : ''}
        </div>
      </div>

      {fundCall.lines.map(line => {
        const send = sendInfo(line);
        return (
          <div key={line.id} className={styles.row}>
            <div className={styles.identity}>
              <Avatar
                src={line.party.profilePictureUrl}
                color="initials"
                name={line.party.displayName}
                radius="xl"
                size={36}
              />
              <div className={styles.identityText}>
                <div className={styles.name}>
                  {line.party.displayName}
                  {line.party.type === SettlementTargetDtoTypeEnum.PlaceholderMember && (
                    <Badge variant="light" color="gray" size="xs">
                      {t('fundCalls.details.notRegistered', 'Non inscrit')}
                    </Badge>
                  )}
                </div>
                {line.householdName && <div className={styles.meta}>{line.householdName}</div>}
                {line.transferReference && (
                  <div className={styles.meta}>
                    {t('fundCalls.details.reference', 'Réf. {{reference}}', {
                      reference: line.transferReference,
                    })}
                  </div>
                )}
                {send && (
                  <div className={send.failed ? styles.sendFailed : styles.meta}>{send.label}</div>
                )}
              </div>
            </div>

            <div className={styles.trailing}>
              {renderAmount(line)}

              {renderStatusBadge(line)}

              <div className={styles.actions}>
                {line.status !== FundCallLineDtoStatusEnum.Settled &&
                  line.status !== FundCallLineDtoStatusEnum.Excluded && (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate('../new-transfer', { state: recordPaymentState(line) })
                      }
                    >
                      {t('fundCalls.details.recordPayment', 'Enregistrer le versement')}
                    </Button>
                  )}

                {canCopyReminder(line) && (
                  <CopyTextButton
                    text={line.reminderText}
                    mixpanelSource="fund_call"
                    label={t('fundCalls.details.copyReminder', 'Copier le message de relance')}
                  />
                )}

                {line.emailSentAt || line.status !== FundCallLineDtoStatusEnum.NotSent ? (
                  <Tooltip label={t('fundCalls.details.openPdf', 'Ouvrir le PDF')}>
                    <ActionIcon
                      variant="outline"
                      size="lg"
                      aria-label={t('fundCalls.details.openPdf', 'Ouvrir le PDF')}
                      onClick={() => openPdf(line.id)}
                    >
                      <IconFileText size={18} />
                    </ActionIcon>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FundCallDetails;
